import { useQuery } from '@tanstack/react-query';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import Api from '@barboxx/api';
import { Category } from '@barboxx/models/types';

interface Props {
    disabled?: boolean;
}

interface CategoryWatchProps {
    category?: Category;
}

export function CategoryInput({ disabled = false }: Props) {
    const { category } = useFormContext<CategoryWatchProps>().getValues();

    const categoryQueryResult = useQuery({
        queryKey: ['categories'],
        queryFn: () =>
            Api.Categories.Category.getCategories({
                page: 0,
                size: 1000,
                sort: 'name,asc',
            }),
        initialData: {
            ok: true,
            problem: null,
            originalError: null,
            data: {
                results: category ? [category] : [],
                total: 1,
                isPaginated: false,
            },
        },
    });

    return (
        <AutocompleteElement
            label="Category"
            name="categoryId"
            loading={categoryQueryResult.isLoading}
            multiple={false}
            options={categoryQueryResult.data?.data?.results ?? []}
            transform={{
                output(event, value, reason, details) {
                    if (reason === 'selectOption') {
                        return typeof value === 'string' ? value : value?.id;
                    }
                    if (reason === 'removeOption') {
                        return null;
                    }
                    return value;
                },
            }}
            autocompleteProps={{
                fullWidth: true,
                readOnly: disabled,
                disabled: disabled,
                isOptionEqualToValue(
                    option?: Category,
                    value?: Category | number | null
                ) {
                    const isNumber = typeof value === 'number';

                    if (isNumber) {
                        return option?.id === value;
                    } else {
                        return option?.id === (value?.id ?? value);
                    }
                },
                getOptionLabel(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    if (option?.business?.place) {
                        return `${option.business?.place.name} - ${option?.name}`;
                    }
                    return `${option?.name}`;
                },
                getOptionKey(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.id}`;
                },
            }}
        />
    );
}
